<template>
  <div>
    <div v-if="$auth.policies['view-meal-config']" class="pa-4">
      <div class="d-flex justify-space-between align-center">
        <Breadcrumbs
          :headline="slot.title || 'Chi tiết khóa'"
          :link="[
            { text: 'Dịch vụ bếp', href: '#' },
            { text: 'Khóa đăng ký ăn', href: '../meal-locks' },
            { text: slot.title || '' }
          ]"
        />
        <div>
          <v-btn outlined color="primary" class="mr-2" @click="openEdit"><v-icon left>edit</v-icon>Sửa thông tin</v-btn>
          <v-btn outlined color="red" @click="confirmRemove"><v-icon left>delete</v-icon>Xóa khóa</v-btn>
        </div>
      </div>

      <!-- Thông tin khóa -->
      <v-card outlined class="pa-4 my-4 elevation-0">
        <v-col>
          <v-row>
            <v-col cols="6" md="4"><b>Tên bữa:</b> </v-col>
            <v-col>{{ slot.title }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="6" md="4"><b>Thời điểm:</b> </v-col>
            <v-col>
              {{ timeOfDayLabel(slot.timeOfDay) }}
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" md="4"><b>Ngày phục vụ:</b> </v-col>
            <v-col>
              {{ serveDaysLabel(slot.serveDays) }}
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6" md="4"><b>Nhóm bị khóa:</b> </v-col>
            <v-col>
              {{ group.title }}
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" md="4"><b>Từ ngày:</b> </v-col>
            <v-col>
              {{ lock.fromDate | ddmmyyyy }}
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" md="4"><b>Đến ngày:</b> </v-col>
            <v-col>
              {{ lock.toDate ? $options.filters.ddmmyyyy(lock.toDate) : NO_END_DATE_LABEL }}
            </v-col>
          </v-row>
        </v-col>
      </v-card>

      <!-- Lịch sử chỉnh sửa của khóa này -->
      <MealConfigHistory v-if="lock.id" ref="history" entity="meal-lock" :entity-id="lock.id" />

      <!-- Dialog sửa khóa -->
      <v-dialog v-model="dialog" width="600" :fullscreen="$vuetify.breakpoint.smAndDown">
        <v-card>
          <v-card-title class="primary white--text">
            <span>SỬA KHÓA</span><v-spacer /><v-icon color="white" @click="dialog = false">close</v-icon>
          </v-card-title>
          <v-form ref="form" class="pa-4" @keydown.native.enter.prevent="save">
            <v-autocomplete
              v-model="form.meal_slot"
              :items="fixedMeals"
              item-text="title"
              item-value="id"
              :label="MEAL_TYPE_FULL_LABEL.fixed"
              outlined
              dense
              :rules="[$rules.required]"
            />
            <v-autocomplete
              v-model="form.meal_group"
              :items="groups"
              item-text="title"
              item-value="id"
              label="Nhóm đối tượng bị khóa"
              hint="Có thể chọn bất kỳ nhóm: toàn trường / cấp / khối / lớp / nhóm tự tạo"
              persistent-hint
              outlined
              dense
              :rules="[$rules.required]"
            />
            <v-row class="mt-1">
              <v-col cols="12" md="6">
                <DateIOSPicker label="Từ ngày *" outlined dense :date.sync="form.fromDate" />
              </v-col>
              <v-col cols="12" md="6">
                <DateIOSPicker label="Đến ngày (tùy chọn)" outlined dense :date.sync="form.toDate" />
              </v-col>
            </v-row>
          </v-form>
          <v-alert dense text type="info" class="caption mx-4 mb-0">{{ effectNote }}</v-alert>
          <v-card-actions>
            <v-btn outlined @click="dialog = false">Hủy</v-btn>
            <v-spacer />
            <v-btn color="primary" depressed :loading="saving" @click="save">Lưu</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <Page403 v-else />
  </div>
</template>

<script>
import Breadcrumbs from '@/components/layout/Breadcrumbs'
import Page403 from '@/views/Page403.vue'
import DateIOSPicker from '@/components/basic/picker/DateIOSPicker.vue'
import MealConfigHistory from '@/modules/mealConfig/MealConfigHistory'
import { MealSlot, MealGroup } from '@/plugins/api'
import { mapActions } from 'vuex'
import moment from 'moment'
import {
  timeOfDayLabel,
  serveDaysLabel,
  MEAL_TYPE_FULL_LABEL,
  NO_END_DATE_LABEL,
  EFFECT_NOTE
} from '@/modules/mealConfig/mealConfigConstants'

export default {
  components: { Breadcrumbs, Page403, DateIOSPicker, MealConfigHistory },
  filters: {
    ddmmyyyy(v) {
      return v ? moment(v).format('DD/MM/YYYY') : ''
    }
  },
  data() {
    return {
      lock: {},
      dialog: false,
      saving: false,
      fixedMeals: [],
      groups: [],
      MEAL_TYPE_FULL_LABEL,
      NO_END_DATE_LABEL,
      effectNote: EFFECT_NOTE,
      form: { meal_slot: null, meal_group: null, fromDate: null, toDate: null }
    }
  },
  computed: {
    slot() {
      return this.lock.meal_slot || {}
    },
    group() {
      return this.lock.meal_group || {}
    }
  },
  created() {
    this.refresh()
  },
  methods: {
    ...mapActions('mealLock', { fetchLock: 'fetchOne', updateLock: 'update', removeLock: 'remove' }),
    timeOfDayLabel,
    serveDaysLabel,
    async refresh() {
      try {
        this.lock = await this.fetchLock(this.$route.params.id)
      } catch (e) {
        this.$alert.error('Không tải được khóa: ' + e)
      }
    },
    async loadOptions() {
      const [fixedMeals, groups] = await Promise.all([
        MealSlot.fetch({ type: 'fixed', _limit: -1 }),
        MealGroup.fetch({ _limit: -1, _sort: 'title:ASC' })
      ])
      this.fixedMeals = fixedMeals
      this.groups = groups
    },
    async openEdit() {
      await this.loadOptions()
      this.form = {
        meal_slot: this.lock.meal_slot && this.lock.meal_slot.id,
        meal_group: this.lock.meal_group && this.lock.meal_group.id,
        fromDate: this.lock.fromDate ? moment(this.lock.fromDate).format('YYYY-MM-DD') : null,
        toDate: this.lock.toDate ? moment(this.lock.toDate).format('YYYY-MM-DD') : null
      }
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    async save() {
      if (!this.$refs.form.validate()) return
      this.saving = true
      try {
        await this.updateLock({
          id: this.lock.id,
          meal_slot: this.form.meal_slot,
          meal_group: this.form.meal_group,
          fromDate: this.form.fromDate,
          toDate: this.form.toDate || null
        })
        this.$alert.updateSuccess()
        this.dialog = false
        await this.refresh()
        if (this.$refs.history) this.$refs.history.refresh()
      } catch (err) {
        this.$alert.error('Lưu khóa thất bại: ' + (err.response ? err.response.data.message : err))
      } finally {
        this.saving = false
      }
    },
    confirmRemove() {
      this.$dialog.confirm({
        title: 'Xác nhận',
        text: 'Xóa khóa này?',
        okText: 'Xóa',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.removeLock(this.lock.id)
            this.$alert.deleteSuccess()
            this.$router.push('/meal-locks')
          } catch (e) {
            this.$alert.deleteError(e)
          } finally {
            this.$loading.active = false
          }
        }
      })
    }
  }
}
</script>
